import { useSearchParams } from "react-router";
import { AlertTriangle, Inbox, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { EmptyState } from "@/components/shared/empty-state";
import { cn, formatRelative } from "@/lib/utils";
import {
  useAdminPendingLeads,
  useDrainPendingLeads,
} from "./use-admin-pending-leads";
import type { AdminPendingLeadRow, PendingLeadStatus } from "./types";

// Admin queued-leads queue. pending_leads is the dead-letter queue partner
// "Register a deal" submissions land in when the HubSpot Lead pipeline isn't
// configured or a push fails; the daily cron-pending-leads-drain retries them.
// This read-only surface gives staff visibility into stuck/errored leads, plus
// a manual "Retry now" that reuses the same drain.
//
// URL state: ?status=pending|pushed|errored (chip filter).

const STATUS_OPTIONS: ReadonlyArray<{
  value: "all" | PendingLeadStatus;
  label: string;
}> = [
  { value: "all", label: "All" },
  { value: "pending", label: "Pending" },
  { value: "errored", label: "Errored" },
  { value: "pushed", label: "Pushed" },
];

function isPendingLeadStatus(value: string): value is PendingLeadStatus {
  return value === "pending" || value === "pushed" || value === "errored";
}

export function PendingLeadsQueuePage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const statusRaw = searchParams.get("status");
  const status: PendingLeadStatus | null =
    statusRaw && isPendingLeadStatus(statusRaw) ? statusRaw : null;

  const query = useAdminPendingLeads({ status });
  const drain = useDrainPendingLeads();
  const rows = query.data ?? [];

  function setStatus(next: string) {
    const sp = new URLSearchParams(searchParams);
    if (next === "all") sp.delete("status");
    else sp.set("status", next);
    setSearchParams(sp, { replace: true });
  }

  return (
    <div>
      <header className="mb-7 flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
            Admin
          </p>
          <h1 className="mt-1 font-display text-3xl font-semibold tracking-tight">
            Queued leads
          </h1>
          <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
            Partner-registered leads waiting to reach HubSpot. They queue here
            when the Lead pipeline isn&rsquo;t configured or a push fails, and
            retry automatically each day.
          </p>
        </div>
        <Button
          variant="outline"
          onClick={() => drain.mutate()}
          disabled={drain.isPending}
        >
          <RefreshCw
            className={cn("h-4 w-4", drain.isPending && "animate-spin")}
          />
          Retry now
        </Button>
      </header>

      <div className="mb-4 flex w-fit flex-wrap items-center gap-1 rounded-md border bg-card p-0.5">
        {STATUS_OPTIONS.map((opt) => (
          <button
            key={opt.value}
            type="button"
            onClick={() => setStatus(opt.value)}
            className={cn(
              "rounded px-2.5 py-1 text-xs font-medium transition-colors",
              (statusRaw ?? "all") === opt.value
                ? "bg-foreground text-background"
                : "text-muted-foreground hover:text-foreground",
            )}
          >
            {opt.label}
          </button>
        ))}
      </div>

      {query.isLoading ? (
        <Skeleton className="h-40 w-full rounded-xl" />
      ) : query.isError ? (
        <EmptyState
          icon={AlertTriangle}
          title="Couldn't load queued leads"
          description={query.error?.message ?? "Try refreshing."}
        />
      ) : rows.length > 0 ? (
        <PendingLeadsTable rows={rows} />
      ) : (
        <EmptyState
          icon={Inbox}
          title={statusRaw ? "No leads in this state" : "No queued leads"}
          description="When everything's reaching HubSpot, this queue stays empty."
        />
      )}
    </div>
  );
}

function PendingLeadsTable({ rows }: { rows: AdminPendingLeadRow[] }) {
  return (
    <div className="overflow-hidden rounded-xl border bg-card">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b bg-muted/30 text-left text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
            <th className="px-4 py-2.5">Lead</th>
            <th className="px-4 py-2.5">Partner</th>
            <th className="px-4 py-2.5">Status</th>
            <th className="px-4 py-2.5">Detail</th>
            <th className="px-4 py-2.5 text-right">Queued</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border/70">
          {rows.map((r) => (
            <tr key={r.id} className="transition-colors hover:bg-muted/30">
              <td className="px-4 py-3">
                <span className="font-medium text-foreground">
                  {r.lead_label}
                </span>
                {r.source_form_slug && (
                  <span className="ml-2 text-xs text-muted-foreground">
                    {r.source_form_slug}
                  </span>
                )}
              </td>
              <td className="px-4 py-3 text-muted-foreground">
                {r.partner_name ?? "—"}
              </td>
              <td className="px-4 py-3">
                <StatusPill status={r.status} />
              </td>
              <td className="px-4 py-3 text-xs text-muted-foreground">
                {r.status === "pushed" && r.hubspot_lead_id ? (
                  `HubSpot lead ${r.hubspot_lead_id}`
                ) : r.error ? (
                  <span className="text-rose-600 dark:text-rose-400">
                    {r.error}
                  </span>
                ) : (
                  "—"
                )}
              </td>
              <td className="px-4 py-3 text-right text-xs text-muted-foreground">
                {formatRelative(r.created_at)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function StatusPill({ status }: { status: PendingLeadStatus }) {
  return (
    <span
      className={cn(
        "inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium capitalize",
        status === "pending" &&
          "bg-amber-100 text-amber-900 dark:bg-amber-500/10 dark:text-amber-300",
        status === "pushed" &&
          "bg-emerald-100 text-emerald-900 dark:bg-emerald-500/10 dark:text-emerald-300",
        status === "errored" &&
          "bg-rose-100 text-rose-900 dark:bg-rose-500/10 dark:text-rose-300",
      )}
    >
      {status}
    </span>
  );
}
